import React from 'react'

import {
  AlignItems,
  Body2,
  Body3,
  Box,
  Color,
  Display,
  FlexDirection,
  Heading4,
  Icon,
  IconVariant,
  Radio,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import type { LandingExperienceOption } from '@library/api-customer-engagement-experience'

type OptionCardTagProps = {
  label: string
}

type OptionCardProps = {
  option: LandingExperienceOption
  isSelected: boolean
  onSelect: () => void
}

const OptionCardWrapper = withCitrusTheme(Box, () => ({
  borderRadius: '8px',
  transition: 'box-shadow 150ms ease-in-out',
  '& > label': {
    borderRadius: '8px',
    padding: '16px 12px',
  },
  '&:hover': {
    boxShadow: '0 12px 16px -4px rgba(33, 41, 48, 0.06), 0 4px 6px -2px rgba(33, 41, 48, 0.03)',
  },
}))

const OptionCardTag = ({ label }: OptionCardTagProps) => (
  <Box
    bg={Color.NeutralGrey_50}
    borderRadius={1}
    paddingV={0.5}
    paddingH={2}
    display={Display.Flex}
    alignItems={AlignItems.Center}
  >
    <Icon name="tick" variant={IconVariant.Confirmation} size={4} />
    <Space direction="horizontal" size={1} />
    <Body3 size={[0, null, 1]} color={Color.ColdGrey_800}>
      {label}
    </Body3>
  </Box>
)

export const OptionCard = ({ option, isSelected, onSelect }: OptionCardProps) => {
  const summaryId = `gousto-anytime-option-${option.id}-summary`
  const tagsId = `gousto-anytime-option-${option.id}-tags`

  return (
    <OptionCardWrapper data-testid={`option-${option.id}`}>
      <Radio
        id={`gousto-anytime-option-${option.id}`}
        name="gousto-anytime-option"
        checked={isSelected}
        onChange={onSelect}
        alignItems={AlignItems.FlexStart}
        outline
        aria-describedby={`${summaryId} ${tagsId}`}
      >
        <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={3}>
          <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={1}>
            <Heading4 color={Color.ColdGrey_900} size={[4, null, 5]}>
              {option.title}
            </Heading4>
            <Body2 id={summaryId} color={Color.ColdGrey_700} size={[2, null, 4]}>
              {option.summary}
            </Body2>
          </Box>
          <Box
            id={tagsId}
            display={Display.Flex}
            flexDirection={FlexDirection.Row}
            gap={2}
            style={{ flexWrap: 'wrap' }}
          >
            {option.tags.map((tag) => (
              <OptionCardTag key={tag} label={tag} />
            ))}
          </Box>
        </Box>
      </Radio>
    </OptionCardWrapper>
  )
}
